import React, { useState, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Radio,
  Chip,
  InputAdornment,
} from '@mui/material';
import { Search, AccountTree, Home } from '@mui/icons-material';
import { SimpleDialog } from '../../../toolympus/components/primitives';
import { EditItemProps } from '../../../toolympus/api/useNewItem';
import { CopTreeNode, MoveTreeNodeItem } from './useCopTreeNodes';

interface Props
  extends Pick<
    EditItemProps<MoveTreeNodeItem>,
    'item' | 'update' | 'errors' | 'save' | 'isLoading'
  > {
  isOpen: boolean;
  close: () => void;
  allNodes: CopTreeNode[];
}

export const MoveTreeNodeDialog: React.FC<Props> = ({
  isOpen,
  close,
  item,
  update,
  save,
  allNodes,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const movingNode = item?.node;
  const selectedTargetParent = item?.targetParent ?? null;

  React.useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  const nodesById = useMemo(() => {
    const map = new Map<number, CopTreeNode>();
    allNodes.forEach(n => map.set(n._id, n));
    return map;
  }, [allNodes]);

  // Exclude self and all descendants from target candidates
  const eligibleNodes = useMemo(() => {
    if (!movingNode) return [];

    const nodeIdStr = String(movingNode._id);

    return allNodes.filter(target => {
      if (target._id === movingNode._id) return false;
      if (target.path) {
        const segments = target.path.split('.');
        if (segments.includes(nodeIdStr)) {
          return false;
        }
      }
      return true;
    });
  }, [allNodes, movingNode]);

  const filteredNodes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return eligibleNodes;

    return eligibleNodes.filter(
      n =>
        n.title?.toLowerCase().includes(term) ||
        n.type?.toLowerCase().includes(term) ||
        String(n._id).includes(term)
    );
  }, [eligibleNodes, searchTerm]);

  const getBreadcrumbLabel = (target: CopTreeNode) => {
    if (!target.path) return '';
    return target.path
      .split('.')
      .map(id => nodesById.get(Number(id))?.title || id)
      .join(' / ');
  };

  return (
    <SimpleDialog
      dialogTitle={
        movingNode ? (
          <FormattedMessage
            id="cop_tree.move_title"
            defaultMessage="Move: {title}"
            values={{ title: movingNode.title }}
          />
        ) : (
          'Move Node'
        )
      }
      isOpen={isOpen}
      close={close}
      save={save}
      maxWidth="sm"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minHeight: 380 }}>
        <TextField
          size="small"
          placeholder="Filter target parents..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          autoFocus
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <List
          dense
          sx={{
            flex: 1,
            maxHeight: 320,
            overflowY: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 0,
          }}
        >
          {/* Root Level Option */}
          <ListItemButton
            selected={selectedTargetParent === null}
            onClick={() => update({ targetParent: null })}
            sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              <Radio
                edge="start"
                checked={selectedTargetParent === null}
                tabIndex={-1}
                disableRipple
                size="small"
              />
            </ListItemIcon>
            <ListItemIcon sx={{ minWidth: 28 }}>
              <Home fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Root Level (No Parent)
                </Typography>
              }
              secondary="Places node at the top level of the tree"
            />
          </ListItemButton>

          {/* Sibling / Parent Candidates */}
          {filteredNodes.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No matching parent nodes found
              </Typography>
            </Box>
          ) : (
            filteredNodes.map(target => {
              const isSelected = selectedTargetParent?._id === target._id;
              const breadcrumb = getBreadcrumbLabel(target);

              return (
                <ListItemButton
                  key={target._id}
                  selected={isSelected}
                  onClick={() => update({ targetParent: target })}
                  sx={{ borderBottom: '1px solid', borderColor: 'action.hover' }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Radio
                      edge="start"
                      checked={isSelected}
                      tabIndex={-1}
                      disableRipple
                      size="small"
                    />
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <AccountTree fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: isSelected ? 600 : 400 }}
                        >
                          {target.title}
                        </Typography>
                        {target.type && (
                          <Chip
                            label={target.type}
                            size="small"
                            sx={{ height: 16, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={breadcrumb || undefined}
                  />
                </ListItemButton>
              );
            })
          )}
        </List>
      </Box>
    </SimpleDialog>
  );
};